import { useEffect, useRef, useCallback } from "react";
import type { Category } from "@yamber/rules-engine";

interface AnnounceModalProps {
  open: boolean;
  onAnnounce: (category: Category) => void;
  onCancel: () => void;
}

const CATEGORIES: Array<{ value: Category; label: string }> = [
  { value: "ones", label: "Ones" },
  { value: "twos", label: "Twos" },
  { value: "threes", label: "Threes" },
  { value: "fours", label: "Fours" },
  { value: "fives", label: "Fives" },
  { value: "sixes", label: "Sixes" },
  { value: "max", label: "Max" },
  { value: "min", label: "Min" },
  { value: "straight", label: "Straight" },
  { value: "fullHouse", label: "Full House" },
  { value: "poker", label: "Poker" },
  { value: "yamb", label: "Yamb" },
];

export function AnnounceModal({ open, onAnnounce, onCancel }: AnnounceModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstButtonRef = useRef<HTMLButtonElement>(null);

  // Focus first button when modal opens
  useEffect(() => {
    if (open && firstButtonRef.current) {
      firstButtonRef.current.focus();
    }
  }, [open]);

  // Handle escape key
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
      }
    },
    [onCancel]
  );

  if (!open) return null;

  return (
    <div
      className="modal-overlay"
      onClick={onCancel}
      role="presentation"
    >
      <div
        ref={modalRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="announce-modal-title"
        aria-describedby="announce-modal-description"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <h2 id="announce-modal-title" className="modal__title">Announce Category</h2>
        <p id="announce-modal-description" className="modal__description">
          Choose a category to announce. You must score in this category.
        </p>
        <div className="modal__categories" role="listbox" aria-label="Categories">
          {CATEGORIES.map(({ value, label }, index) => (
            <button
              key={value}
              ref={index === 0 ? firstButtonRef : undefined}
              type="button"
              role="option"
              className="modal__category-btn"
              onClick={() => onAnnounce(value)}
              aria-label={`Announce ${label}`}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="modal__cancel-btn"
          onClick={onCancel}
          aria-label="Cancel announcement"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
